import React, { useState, useEffect } from "react";
import Pagination from "../../components/Pagination.tsx";
import { useAllQueries, useUpdateQueryStatus } from "../../hooks/useAdmin.ts";
import { useTheme } from "../../contexts/ThemeContext.tsx";
import { getTheme } from '../../theme.ts';

// Define proper types
interface Query {
  id: string;
  description: string;
  created_at: string;
  status: string;
}

interface QueriesData {
  data?: Query[];
}

const ViewQueries: React.FC = () => {
  const { themeName } = useTheme();
  const currentTheme = getTheme(themeName);
  const { data: queriesData, isLoading, isError } = useAllQueries();
  const updateQueryStatusMutation = useUpdateQueryStatus();

  const [queries, setQueries] = useState<Query[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(3);
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  useEffect(() => {
    if (queriesData) {
      // Handle both direct array and nested data structure
      const queriesArray = Array.isArray(queriesData) 
        ? queriesData 
        : (queriesData as QueriesData)?.data || [];
      setQueries(queriesArray);
    }
  }, [queriesData]);

  const handleStatusChange = async (queryId: string, newStatus: string) => {
    setUpdatingId(queryId);
    try {
      await updateQueryStatusMutation.mutateAsync({ id: queryId, status: newStatus });
      // Optimistic update
      setQueries(prevQueries =>
        prevQueries.map(query =>
          query.id === queryId ? { ...query, status: newStatus } : query
        )
      );
    } catch (error) {
      console.error('Failed to update query status:', error);
    } finally {
      setUpdatingId(null);
    }
  };

  // Pagination calculations
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedQueries = queries.slice(startIndex, endIndex);
  const totalPages = Math.ceil(queries.length / rowsPerPage);

  const getStatusColor = (status: string): string => {
    switch(status?.toUpperCase()) {
      case 'OPEN':
        return currentTheme.primary;
      case 'IN_PROGRESS':
        return currentTheme.warning;
      case 'CLOSED':
        return currentTheme.success;
      default:
        return currentTheme.textSecondary;
    }
  };

  const getStatusIcon = (status: string): string => {
    switch(status?.toUpperCase()) {
      case 'OPEN':
        return '🟢';
      case 'IN_PROGRESS':
        return '🟡';
      case 'CLOSED':
        return '🔴';
      default:
        return '⚪';
    }
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid date';
    }
  };

  return (
    <div className="min-h-screen p-4 md:p-6 lg:p-8" style={{ backgroundColor: currentTheme.background }}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2" style={{ color: currentTheme.primary }}>
            📋 Support Queries
          </h1>
          <p className="text-sm opacity-70" style={{ color: currentTheme.text }}>
            Manage and respond to user support queries
          </p>
        </div>

        {/* Main Content */}
        <div className="rounded-xl shadow-xl overflow-hidden border" style={{ 
          backgroundColor: currentTheme.surface,
          borderColor: currentTheme.border 
        }}>
          {/* Loading State */}
          {isLoading && (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-16 h-16 border-4 border-t-transparent rounded-full animate-spin mb-4" 
                   style={{ borderColor: `${currentTheme.primary}40`, borderTopColor: currentTheme.primary }} />
              <p style={{ color: currentTheme.text }}>Loading queries...</p>
            </div>
          )}

          {/* Error State */}
          {isError && (
            <div className="text-center py-16">
              <div className="text-6xl mb-4">😕</div>
              <p className="text-lg mb-2" style={{ color: currentTheme.error }}>Error loading queries</p>
              <p className="text-sm opacity-60" style={{ color: currentTheme.text }}>
                Please try refreshing the page
              </p>
            </div>
          )}

          {/* Empty State */}
          {!isLoading && !isError && queries.length === 0 && (
            <div className="text-center py-16">
              <div className="text-6xl mb-4">📭</div>
              <p className="text-lg mb-2" style={{ color: currentTheme.text }}>No queries found</p>
              <p className="text-sm opacity-60" style={{ color: currentTheme.text }}>
                Support queries will appear here when users submit them
              </p>
            </div>
          )}

          {/* Queries List */}
          {!isLoading && !isError && queries.length > 0 && (
            <div className="p-4 md:p-6">
              <div className="space-y-4">
                {paginatedQueries.map((query) => (
                  <div
                    key={query.id}
                    className="group relative rounded-lg border transition-all duration-200 hover:shadow-lg hover:scale-[1.01]"
                    style={{ 
                      backgroundColor: currentTheme.surface,
                      borderColor: currentTheme.border
                    }}
                  >
                    {/* Status Indicator Bar */}
                    <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-lg" 
                         style={{ backgroundColor: getStatusColor(query.status) }} />

                    <div className="p-5 pl-6">
                      {/* Header with Status */}
                      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                        <div className="flex items-center gap-2">
                          <span className="text-xl">{getStatusIcon(query.status)}</span>
                          <span className="text-xs font-medium px-2.5 py-1 rounded-full"
                                style={{ 
                                  backgroundColor: `${getStatusColor(query.status)}15`,
                                  color: getStatusColor(query.status),
                                  border: `1px solid ${getStatusColor(query.status)}30`
                                }}>
                            {query.status || 'UNKNOWN'}
                          </span>
                        </div>
                        <span className="text-xs flex items-center gap-1 opacity-60" style={{ color: currentTheme.text }}>
                          <span>🕒</span>
                          {formatDate(query.created_at)}
                        </span>
                      </div>

                      {/* Description */}
                      <p className="text-base mb-4 leading-relaxed" style={{ color: currentTheme.text }}>
                        {query.description}
                      </p>

                      {/* Action Footer */}
                      <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t" 
                           style={{ borderColor: currentTheme.border }}>
                        <div className="flex items-center gap-2">
                          <span className="text-xs opacity-60" style={{ color: currentTheme.text }}>
                            Update status:
                          </span>
                          <select
                            value={query.status}
                            onChange={(e) => handleStatusChange(query.id, e.target.value)}
                            disabled={updatingId === query.id}
                            className="px-3 py-1.5 rounded-lg text-sm font-medium transition-all cursor-pointer hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                            style={{
                              backgroundColor: `${getStatusColor(query.status)}10`,
                              color: getStatusColor(query.status),
                              border: `1px solid ${getStatusColor(query.status)}30`,
                            }}
                          >
                            <option value="OPEN" style={{ color: currentTheme.primary }}>🟢 OPEN</option>
                            <option value="IN_PROGRESS" style={{ color: currentTheme.warning }}>🟡 IN PROGRESS</option>
                            <option value="CLOSED" style={{ color: currentTheme.success }}>🔴 CLOSED</option>
                          </select>
                        </div>
                        
                        {updatingId === query.id && (
                          <span className="text-xs animate-pulse" style={{ color: currentTheme.primary }}>
                            Updating...
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination Controls */}
              <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t"
                   style={{ borderColor: currentTheme.border }}>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm" style={{ color: currentTheme.textSecondary }}>
                      Show:
                    </span>
                    <select
                      value={rowsPerPage}
                      onChange={(e) => {
                        setRowsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      className="px-3 py-1.5 rounded-lg text-sm border focus:outline-none focus:ring-2 transition-shadow"
                      style={{
                        backgroundColor: currentTheme.surface,
                        color: currentTheme.text,
                        borderColor: currentTheme.border,
                      }}
                    >
                      <option value={3}>3 per page</option>
                      <option value={10}>10 per page</option>
                      <option value={25}>25 per page</option>
                      <option value={50}>50 per page</option>
                    </select>
                  </div>
                  
                  <span className="text-sm px-3 py-1.5 rounded-lg" 
                        style={{ backgroundColor: `${currentTheme.primary}10`, color: currentTheme.primary }}>
                    {startIndex + 1}-{Math.min(endIndex, queries.length)} of {queries.length}
                  </span>
                </div>

                <Pagination 
                  currentPage={currentPage} 
                  totalPages={totalPages} 
                  onPageChange={setCurrentPage} 
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewQueries;